/**
 * PATH: packages/email-hub/src/_internal/flags/index.ts
 * FILE_ID: EMAILHUB-INTERNAL-FLAGS-001
 * PURPOSE: One fail-closed client rollout gate for Email Hub consumers.
 * WHY: Client flags hide additive consumers during rollback; they never grant
 *      server authority or bypass organisation/mailbox policy.
 * FLOW: typed flag -> server override or Vite env -> explicit/default state.
 */

export type EmailHubFeatureFlag =
  | 'evidenceV1'
  | 'responsiveV1'
  | 'providerWritebackRestore'
  | 'intelligenceActions'
  | 'intelligenceAutoApply'
  | 'generatedE2EProbe'
  | 'wholeReaderHeader'

interface EmailHubFeatureFlagDefinition {
  envKey: string
  defaultEnabled: boolean
}

export const EMAIL_HUB_FEATURE_FLAGS: Readonly<
  Record<EmailHubFeatureFlag, EmailHubFeatureFlagDefinition>
> = {
  evidenceV1: {
    envKey: 'VITE_EMAIL_HUB_EVIDENCE_V1',
    defaultEnabled: true,
  },
  responsiveV1: {
    envKey: 'VITE_EMAIL_HUB_RESPONSIVE_V1',
    defaultEnabled: false,
  },
  providerWritebackRestore: {
    envKey: 'VITE_EMAIL_HUB_WRITEBACK_RESTORE',
    defaultEnabled: false,
  },
  intelligenceActions: {
    envKey: 'VITE_EMAIL_HUB_INTELLIGENCE_ACTIONS',
    defaultEnabled: false,
  },
  intelligenceAutoApply: {
    envKey: 'VITE_EMAIL_HUB_INTELLIGENCE_AUTO_APPLY',
    defaultEnabled: false,
  },
  generatedE2EProbe: {
    envKey: 'VITE_EMAIL_HUB_GENERATED_E2E',
    defaultEnabled: false,
  },
  wholeReaderHeader: {
    envKey: 'VITE_EMAIL_HUB_WHOLE_READER_HEADER',
    // Rollback contract (plan D269 Sprint 0, updated D269 Generation E): the
    // click-everything suite (commit a5d95f720) plus the full Phase 8
    // verification pass now hold with the flag ON, so Calm Command Header is
    // the default. VITE_EMAIL_HUB_WHOLE_READER_HEADER=false remains the
    // immediate kill switch back to the legacy reader stack - do not remove
    // the override path even after this default flip.
    defaultEnabled: true,
  },
}

export interface EmailHubFlagContext {
  /** Server-hydrated rollout state. False is an immediate client kill switch. */
  overrides?: Partial<Record<EmailHubFeatureFlag, boolean>>
  /** Injectable for tests; defaults to Vite build-time environment. */
  env?: Readonly<Record<string, string | boolean | undefined>>
}

function parseBooleanFlag(value: string | boolean | undefined): boolean | null {
  if (typeof value === 'boolean') return value
  if (typeof value !== 'string') return null
  const normalized = value.trim().toLowerCase()
  if (['1', 'true', 'yes', 'on'].includes(normalized)) return true
  if (['0', 'false', 'no', 'off'].includes(normalized)) return false
  return null
}

export function isEmailHubFeatureEnabled(
  flag: EmailHubFeatureFlag,
  context: EmailHubFlagContext = {},
): boolean {
  const override = context.overrides?.[flag]
  if (typeof override === 'boolean') return override

  const definition = EMAIL_HUB_FEATURE_FLAGS[flag]
  const env =
    context.env ??
    (import.meta.env as unknown as Record<string, string | boolean | undefined>)
  return parseBooleanFlag(env[definition.envKey]) ?? definition.defaultEnabled
}

